import React from 'react';
import { useDropzone } from 'react-dropzone';
import { toastHelper } from '../../../utils/toastHelper';
import { cn } from '@/lib/utils';

interface DropzoneFieldProps {
  onDrop: (files: File[]) => void;
  label: React.ReactNode;
  file: File | null;
  acceptedFileTypes: Record<string, string[]>;
  maxFileSize: number;
  isValid: boolean;
  setIsValid: (valid: boolean) => void;
  isScheduled?: boolean;
  tierDisplayFormats?: string; // For better error messages
}

function DropzoneField({
  onDrop,
  label,
  file,
  acceptedFileTypes,
  maxFileSize,
  isValid,
  setIsValid,
  isScheduled = false,
  tierDisplayFormats,
}: DropzoneFieldProps) {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        handleValidationErrors(fileRejections, acceptedFileTypes, maxFileSize, tierDisplayFormats);
        setIsValid(false);
        return;
      }
      if (acceptedFiles.length > 0) {
        onDrop(acceptedFiles.slice(0, 1));
        setIsValid(true);
        toastHelper.newMessage('success', 'Success.', 'File attached successfully!');
      }
    },
    accept: acceptedFileTypes,
    maxFiles: 1,
    maxSize: maxFileSize,
  });

  const handleValidationErrors = (fileRejections: any[], acceptedFileTypes: Record<string, string[]>, maxFileSize: number, tierDisplayFormats?: string) => {
    fileRejections.forEach(({ errors }) => {
      errors.forEach((error: any) => {
        if (error.code === 'file-invalid-type') {
          const formats = tierDisplayFormats || Object.values(acceptedFileTypes).flat().join(', ');
          toastHelper.newMessage(
            'error',
            'Unsupported format',
            `This file format is not supported for your subscription tier. Supported formats: ${formats}. Upgrade your plan to access more formats.`
          );
        } else if (error.code === 'file-too-large') {
          const sizeMB = maxFileSize / (1024 * 1024);
          toastHelper.newMessage(
            'error',
            'File too large',
            `File size exceeds your tier's limit of ${sizeMB}MB. Upgrade your plan for larger file support.`
          );
        }
      });
    });
  };

  const truncateFileName = (fileName: string, maxLength: number = 50) => {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.substring(fileName.lastIndexOf('.'));
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 3);
    return `${truncatedName}...${extension}`;
  };

  return (
    <div
      {...getRootProps()}
      className={cn(
        "border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors h-[80px] flex items-center justify-center",
        !isValid && "border-destructive",
        isValid && file && (!isScheduled ? "border-primary" : "border-secondary"),
        isValid && !file && (!isScheduled ? "border-border hover:border-primary" : "border-border hover:border-secondary")
      )}
    >
      <input {...getInputProps()} />
      {file ? (
        <p className="text-sm font-medium text-foreground px-2 line-clamp-2">
          {truncateFileName(file.name)}
        </p>
      ) : (
        <div>{label}</div>
      )}
    </div>
  );
}

export default DropzoneField;
